import type { DeliveryNote } from '../delivery-notes/api';
import type { QueuedSignature, SyncSummary } from './signature-queue';

export interface QueueSnapshot {
  /** Items of the logged in user, pending and problem. */
  items: QueuedSignature[];
  syncing: boolean;
  /** The last attempt was refused for lack of a valid session: the user has to log in again. */
  authRequired: boolean;
}

export interface QueueControllerDeps {
  list: (userId: string) => Promise<QueuedSignature[]>;
  sync: (userId: string) => Promise<SyncSummary>;
  dismiss: (userId: string, noteId: string) => Promise<void>;
  getUserId: () => string | null;
  onSynced?: (notes: DeliveryNote[]) => void;
  intervalMs?: number;
}

const EMPTY: QueueSnapshot = { items: [], syncing: false, authRequired: false };

/**
 * Keeps the outbox moving: syncs on start, when the browser comes back
 * online and every minute while something is pending, and exposes the state
 * (an external store, so React can subscribe to it).
 */
export function createQueueController({
  list,
  sync,
  dismiss,
  getUserId,
  onSynced,
  intervalMs = 60_000,
}: QueueControllerDeps) {
  let snapshot: QueueSnapshot = EMPTY;
  const listeners = new Set<() => void>();

  const publish = (next: Partial<QueueSnapshot>): void => {
    snapshot = { ...snapshot, ...next };
    listeners.forEach((listener) => listener());
  };

  const refresh = async (): Promise<void> => {
    const userId = getUserId();

    publish({ items: userId === null ? [] : await list(userId) });
  };

  const syncNow = async (): Promise<void> => {
    const userId = getUserId();

    if (userId === null || snapshot.syncing) {
      return;
    }

    await refresh();

    if (!snapshot.items.some((item) => item.state === 'pending')) {
      publish({ authRequired: false });

      return;
    }

    publish({ syncing: true });

    try {
      const summary = await sync(userId);

      if (summary.synced.length > 0) {
        onSynced?.(summary.synced);
      }

      publish({ authRequired: summary.authRequired });
    } finally {
      publish({ syncing: false });
      await refresh();
    }
  };

  return {
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
    getSnapshot: (): QueueSnapshot => snapshot,
    refresh,
    syncNow,

    dismiss: async (noteId: string): Promise<void> => {
      const userId = getUserId();

      if (userId !== null) {
        await dismiss(userId, noteId);
        await refresh();
      }
    },

    start: (): (() => void) => {
      const trigger = (): void => void syncNow().catch(() => undefined);
      const timer = setInterval(trigger, intervalMs);

      window.addEventListener('online', trigger);
      trigger();

      return () => {
        clearInterval(timer);
        window.removeEventListener('online', trigger);
      };
    },
  };
}

export type QueueController = ReturnType<typeof createQueueController>;
